// Single source of truth for the ORDER and VISIBILITY of shop section tabs on mobile.
// Mirrors the logic inlined in ScrollableNavbar.svelte (desktop) so MobileTabBar
// renders exactly the same tab set. Keep the two in sync until the desktop nav is
// migrated onto this helper in a later phase.

import { adventLabelKey } from './advent-variant.js';

export function shouldDisplayTab(section, packs) {
    const featuredPacks = packs.filter((p) => {
        if (!p.isFeatured) return false;
        return p.storeSection === 'promos' ||
            (p.storeSection === 'goldPacks' && p.specialTitle && p.specialTitle.toLowerCase().includes('primetime'));
    });
    const lowestFeaturedPack = featuredPacks.length > 0
        ? featuredPacks.sort((a, b) => {
            const positionDiff = (a.position || 0) - (b.position || 0);
            if (positionDiff !== 0) return positionDiff;
            if (a.showPlayerName && !b.showPlayerName) return -1;
            if (!a.showPlayerName && b.showPlayerName) return 1;
            return 0;
        })[0]
        : null;

    let sectionPacks;
    if (section === 'promos') {
        sectionPacks = packs.filter((pack) => pack.storeSection === section && !pack.luckyDraw);
    } else {
        sectionPacks = packs.filter((pack) => pack.storeSection === section);
    }
    if (lowestFeaturedPack && lowestFeaturedPack.storeSection === section) {
        sectionPacks = sectionPacks.filter((pack) => pack !== lowestFeaturedPack);
    }
    return sectionPacks.length > 0;
}

function hasFeaturedTab(packs) {
    return packs.filter((pack) => {
        if (!pack.isFeatured) return false;
        return pack.storeSection === 'promos' ||
            (pack.storeSection === 'goldPacks' && pack.specialTitle && pack.specialTitle.toLowerCase().includes('primetime'));
    }).length > 0;
}

export function deriveShopTabs(state) {
    const {
        activeStorePacks = [],
        adventCalendarEvent = {},
        battlePassEvent = {},
        rechargeRewardsEvent = {},
        luckyDrawEvent = {},
        chainPromoEvent = {},
        subscriptions = [],
        vipLevel = 0,
        castleLevel = 0,
        growthFundConfig = null,
        monsterGrowthFundConfig = null,
        claimedGrowthFundPrizes = [],
        claimedMonsterGrowthFundPrizes = [],
        now = 0,
        seen = {},
    } = state;

    const tabs = [];

    if (Object.keys(adventCalendarEvent).length !== 0) {
        tabs.push({
            key: 'adventCalendar',
            labelKey: adventLabelKey(adventCalendarEvent.uniqueIdentifier),
            isNew: seen.adventCalendarSeen !== 'true',
        });
    }

    if (Object.keys(battlePassEvent).length !== 0 && new Date(battlePassEvent.claimBy).getTime() > now) {
        tabs.push({
            key: 'webBattlePass',
            labelKey: 'WebBattlePassShopTab',
            labelParams: { default: 'Season Pass' },
            isNew: seen.webBattlePassSeen !== 'true',
        });
    }

    if (activeStorePacks.length > 0) {
        if (hasFeaturedTab(activeStorePacks)) {
            tabs.push({ key: 'featured', labelKey: 'NewShopTabFeatured' });
        }
        if (shouldDisplayTab('beginnerPacks', activeStorePacks)) {
            tabs.push({ key: 'beginnerPacks', labelKey: 'NewShopTabBeginnerDeals' });
        }
        if (shouldDisplayTab('welcomeBackPacks', activeStorePacks)) {
            tabs.push({ key: 'welcomeBackPacks', labelKey: 'NewShopTabWelcomeBack' });
        }
        if (shouldDisplayTab('promos', activeStorePacks)) {
            tabs.push({ key: 'promos', labelKey: 'NewShopTabPromos' });
        }
        // Chain Deals packs are hidden (no storeSection), so the Events tab is
        // gated on the chain event itself, mirroring ScrollableNavbar.
        const specialEventsVisible = shouldDisplayTab('specialEvents', activeStorePacks)
            || activeStorePacks.some((p) => p.storeSection === 'cardCollection')
            || activeStorePacks.some((p) => p.storeSection === 'pvpArena')
            || activeStorePacks.some((p) => p.storeSection === 'singleJackFever')
            || !!chainPromoEvent?.id;
        if (specialEventsVisible) {
            tabs.push({ key: 'specialEvents', labelKey: 'EventsWindowTitle' });
        }
        if (shouldDisplayTab('dailyDeals', activeStorePacks)) {
            tabs.push({ key: 'dailyDeals', labelKey: 'NewShopTabDailyDeals' });
        }

        const growthLevels = growthFundConfig?.levels ? Object.keys(growthFundConfig.levels) : [];
        const growthAllClaimed = growthLevels.length > 0 && growthLevels.every((l) => claimedGrowthFundPrizes.includes(parseInt(l)));
        const hasGrowth = growthLevels.length > 0 && !growthAllClaimed;
        const monsterLevels = monsterGrowthFundConfig?.levels ? Object.keys(monsterGrowthFundConfig.levels) : [];
        const monsterAllClaimed = monsterLevels.length > 0 && monsterLevels.every((l) => claimedMonsterGrowthFundPrizes.includes(parseInt(l)));
        const hasMonster = monsterLevels.length > 0 && !monsterAllClaimed;
        const hasResourceTrove = activeStorePacks.some((p) => p.storeSection === 'resourceTrove');
        if (hasGrowth || hasMonster || hasResourceTrove) {
            tabs.push({ key: 'advancementRewards', labelKey: 'NewShopTabAdvancementRewards' });
        }

        if (shouldDisplayTab('midweekMadness', activeStorePacks)) {
            tabs.push({ key: 'midweekMadness', labelKey: 'NewShopTabMidweekMadness' });
        }
        if (shouldDisplayTab('webshopExclusive', activeStorePacks)) {
            tabs.push({ key: 'webshopExclusive', labelKey: 'NewShopTabWebshopExclusive' });
        }
        if (Object.keys(rechargeRewardsEvent).length !== 0) {
            tabs.push({ key: 'goldRush', labelKey: 'NewShopTabEventRecharge' });
        }
        if (Object.keys(luckyDrawEvent).length !== 0 && activeStorePacks.filter((pack) => pack.luckyDraw).length > 0) {
            tabs.push({ key: 'luckyDraw', labelKey: 'LuckyDraw' });
        }
        const hasVip = activeStorePacks.filter((pack) => {
            if (pack.storeSection === 'vip') {
                const match = pack.bundleName.match(/lvl(\d+)pack/);
                return match && match[1] <= vipLevel;
            }
            return false;
        }).length > 0;
        if (hasVip) {
            tabs.push({ key: 'vip', labelKey: 'NewShopTabVIP' });
        }
        tabs.push({ key: 'goldAndResources', labelKey: 'NewShopTabGoldAndResources' });
        tabs.push({ key: 'giftFriend', labelKey: 'GiftFriend', locked: castleLevel < 15 });
        tabs.push({ key: 'redeemCode', labelKey: 'RedeemCode' });
    } else if (subscriptions?.length > 0) {
        tabs.push({ key: 'freeGifts', labelKey: 'WebshopHomepageBannerHeader' });
    }

    return tabs;
}
